import { z } from "zod";
import { Transaction } from "../domain.js";
import { Id } from "../primitives.js";

/**
 * What was done to the ledger, and what looks like it was done by mistake.
 *
 * Both halves of this file exist because of one £600 row. A transfer between
 * two of the household's own accounts was filed by hand as income, and every
 * total in the app carried that £600 as money the household had earned. The
 * edit had been recorded all along — nothing read the recording, and nothing
 * ever asked whether a hand-filed row still agreed with everything around it.
 */

export const AuditEntry = z.object({
  id: Id,
  action: z.string(),
  /** Written for a person to read, not for a log sink. */
  summary: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  /** Who did it. Null for anything the system did unattended. */
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  createdAt: z.string(),
  /** What the row was before, where the change recorded it. */
  before: z.string().nullable(),
  /** What it became. */
  after: z.string().nullable(),
});

export const AuditQuery = z.object({
  /** Narrow to one row's history — what the Edit dialog asks for. */
  targetId: z.string().optional(),
  action: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

export const AuditList = z.object({
  data: z.array(AuditEntry),
  nextCursor: z.string().nullable(),
});

/**
 * Why a row is being questioned.
 *
 * - `disagrees-with-rule` — somebody filed it by hand somewhere the rules would
 *   not have put it. Not wrong by itself: overriding a rule is exactly what a
 *   manual category is for. It is worth a look because the override is silent
 *   and permanent, and the rule is what every sibling row follows.
 * - `mirrors-a-transfer` — it is filed as money in or money out, and an exact
 *   negation of it sits in another account within a few days, filed as a
 *   transfer. One of the two is wrong about what happened, and while it stands
 *   the household's income or spending is overstated by the amount.
 */
export const MisfiledReason = z.enum(["disagrees-with-rule", "mirrors-a-transfer"]);

export const MisfiledRow = z.object({
  transaction: Transaction,
  accountLabel: z.string(),
  reason: MisfiledReason,
  /** The category it is filed under now. */
  filedAs: z.string(),
  /**
   * Where the doubt points. The category the rules would have chosen, or the
   * category of the row it mirrors.
   */
  expected: z.string(),
  /** For `mirrors-a-transfer`, the row on the other account. */
  mirrors: Transaction.nullable(),
  mirrorsAccountLabel: z.string().nullable(),
});

export const MisfiledQuery = z.object({
  reason: MisfiledReason.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const MisfiledList = z.object({
  data: z.array(MisfiledRow),
  total: z.number().int(),
  /** Rows the scan stopped at, so a huge ledger says so rather than lying. */
  scanTruncated: z.boolean(),
});

export type AuditEntry = z.infer<typeof AuditEntry>;
export type AuditQuery = z.infer<typeof AuditQuery>;
export type AuditList = z.infer<typeof AuditList>;
export type MisfiledReason = z.infer<typeof MisfiledReason>;
export type MisfiledRow = z.infer<typeof MisfiledRow>;
export type MisfiledQuery = z.infer<typeof MisfiledQuery>;
export type MisfiledList = z.infer<typeof MisfiledList>;
